import initializeInboxRuleEdit from './../initializeInboxRuleEdit';
import getFolderFromId from '../../utils/folderTypeConversionUtil';
import getActionClauseTypeOptions from './getActionClauseTypeOptions';
import getConditionalExceptionClauseTypeOptions from './getConditionalExceptionClauseTypeOptions';
import mapPeopleIdentityToReadWriteRecipientWellViewState from './mapPeopleIdentityToReadWriteRecipientWellViewState';
import setSelectedFolderId from './setSelectedFolderId';
import ClauseValueType from '../../store/schema/ClauseValueType';
import { InboxRulesEditOptionState } from '../../store/schema/InboxRulesEditOptionState';
import store from '../../store/store';
import addNewClause from '../addNewClause';
import ClauseSectionType from '../types/ClauseSectionType';
import InboxRule from 'owa-service/lib/contract/InboxRule';
import { action, select } from 'satcheljs/lib/legacy';

export interface MapInboxRuleClausesToInboxRuleState {
    optionState: InboxRulesEditOptionState;
}

const KB_TO_B = 1024;

/**
 * Maps an inbox rule's properties to inbox rule clauses to populated the edit page
 * @param inboxRule the rule whose properties will be added to the edit store
 */
export default action('mapInboxRuleToInboxRuleClauses')(
    select({
        optionState: () => store.inboxRulesEditState,
    })(function mapInboxRuleToInboxRuleClauses(
        inboxRule: InboxRule,
        state?: MapInboxRuleClausesToInboxRuleState
    ) {
        // map conditional clauses
        let { optionState } = state;
        let clauseSection: ClauseSectionType;
        let conditionalExceptionClauseTypes = getConditionalExceptionClauseTypeOptions();
        let actionClauseTypes = getActionClauseTypeOptions();

        optionState.ruleName = inboxRule.Name;
        optionState.ruleId = inboxRule.Identity ? inboxRule.Identity.RawIdentity : null;
        optionState.ignoreSubsequentRules = inboxRule.StopProcessingRules;

        let hasAddedConditional = false;
        clauseSection = 'conditional';
        for (let conditionalClause of conditionalExceptionClauseTypes) {
            let clauseValueToAdd: ClauseValueType;

            switch (conditionalClause) {
                case 'from':
                    if (inboxRule.From) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.From
                        );
                    }
                    break;
                case 'to':
                    if (inboxRule.SentTo) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.SentTo
                        );
                    }
                    break;
                case 'onTheToOrCcLine':
                    if (inboxRule.MyNameInToOrCcBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'theOnlyRecipientListed':
                    if (inboxRule.SentOnlyToMe) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'onTheToLine':
                    if (inboxRule.MyNameInToBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'onTheCcLine':
                    if (inboxRule.MyNameInCcBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'notOnTheToLine':
                    if (inboxRule.MyNameNotInToBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'subjectIncludes':
                    if (inboxRule.SubjectContainsWords) {
                        clauseValueToAdd = inboxRule.SubjectContainsWords.slice(0);
                    }
                    break;
                case 'subjectOrBodyIncludes':
                    if (inboxRule.SubjectOrBodyContainsWords) {
                        clauseValueToAdd = inboxRule.SubjectOrBodyContainsWords.slice(0);
                    }
                    break;
                case 'inTheBody':
                    if (inboxRule.BodyContainsWords) {
                        clauseValueToAdd = inboxRule.BodyContainsWords.slice(0);
                    }
                    break;
                case 'inTheSenderAddress':
                    if (inboxRule.FromAddressContainsWords) {
                        clauseValueToAdd = inboxRule.FromAddressContainsWords.slice(0);
                    }
                    break;
                case 'inTheRecipientAddress':
                    if (inboxRule.RecipientAddressContainsWords) {
                        clauseValueToAdd = inboxRule.RecipientAddressContainsWords.slice(0);
                    }
                    break;
                case 'inTheMessageHeader':
                    if (inboxRule.HeaderContainsWords) {
                        clauseValueToAdd = inboxRule.HeaderContainsWords.slice(0);
                    }
                    break;
                case 'withImportance':
                    if (inboxRule.WithImportance !== 'NullImportance') {
                        clauseValueToAdd = inboxRule.WithImportance;
                    }
                    break;
                case 'sensitivity':
                    if (inboxRule.WithSensitivity !== 'NullSensitivity') {
                        clauseValueToAdd = inboxRule.WithSensitivity;
                    }
                    break;
                case 'flag':
                    if (inboxRule.RequestedAction !== 'NullInboxRuleMessageFlag') {
                        clauseValueToAdd = inboxRule.RequestedAction;
                    }
                    break;
                case 'type':
                    if (inboxRule.MessageTypeMatches != 'NullInboxRuleMessageType') {
                        clauseValueToAdd = inboxRule.MessageTypeMatches;
                    }
                    break;
                case 'attachment':
                    if (inboxRule.HasAttachment) {
                        clauseValueToAdd = inboxRule.HasAttachment;
                    }
                    break;
                case 'atLeast':
                    if (inboxRule.WithinSizeRangeMinimum) {
                        clauseValueToAdd = inboxRule.WithinSizeRangeMinimum / KB_TO_B;
                    }
                    break;
                case 'atMost':
                    if (inboxRule.WithinSizeRangeMaximum) {
                        clauseValueToAdd = inboxRule.WithinSizeRangeMaximum / KB_TO_B;
                    }
                    break;
                case 'before':
                    if (inboxRule.ReceivedBeforeDate) {
                        clauseValueToAdd = inboxRule.ReceivedBeforeDate;
                    }
                    break;
                case 'after':
                    if (inboxRule.ReceivedAfterDate) {
                        clauseValueToAdd = inboxRule.ReceivedAfterDate;
                    }
                    break;
            }

            // only add clauses to the edit page that have corresponding values on the inbox rule
            if (clauseValueToAdd) {
                addNewClause(clauseSection, conditionalClause, clauseValueToAdd);
                hasAddedConditional = true;
            }
        }

        // If there were no conditional clauses added, that means there were no conditions and that the rule
        // should apply to all messages so the conditionalClause type is 'any'
        if (!hasAddedConditional) {
            addNewClause(clauseSection, 'any', true /* clauseValue */);
        }

        clauseSection = 'action';
        for (let actionClause of actionClauseTypes) {
            let clauseValueToAdd: ClauseValueType;

            switch (actionClause) {
                case 'moveTo':
                    if (inboxRule.MoveToFolder) {
                        let ruleFolderId = inboxRule.MoveToFolder.RawIdentity;
                        // check if the inbox rule folder id is a raw identity or folder ID
                        let ruleFolder = getFolderFromId(ruleFolderId);
                        if (ruleFolder) {
                            setSelectedFolderId(ruleFolder.folderId);
                        }
                        clauseValueToAdd = ruleFolder;
                    }
                    break;
                case 'copyTo':
                    if (inboxRule.CopyToFolder) {
                        let ruleFolderId = inboxRule.CopyToFolder.RawIdentity;
                        // check if the inbox rule folder id is a raw identity or folder ID
                        let ruleFolder = getFolderFromId(ruleFolderId);
                        if (ruleFolder) {
                            setSelectedFolderId(ruleFolder.folderId);
                        }
                        clauseValueToAdd = ruleFolder;
                    }
                    break;
                case 'delete':
                    if (inboxRule.DeleteMessage) {
                        clauseValueToAdd = inboxRule.DeleteMessage;
                    }
                    break;
                case 'pin':
                    if (inboxRule.PinMessage) {
                        clauseValueToAdd = inboxRule.PinMessage;
                    }
                    break;
                case 'read':
                    if (inboxRule.MarkAsRead) {
                        clauseValueToAdd = inboxRule.MarkAsRead;
                    }
                    break;
                case 'markImportance':
                    if (inboxRule.MarkImportance !== 'NullImportance') {
                        clauseValueToAdd = inboxRule.MarkImportance;
                    }
                    break;
                case 'category':
                    if (inboxRule.ApplyCategory) {
                        clauseValueToAdd = inboxRule.ApplyCategory.slice(0);
                    }
                    break;
                case 'forwardTo':
                    if (inboxRule.ForwardTo) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.ForwardTo
                        );
                    }
                    break;
                case 'forwardAsAttachmentTo':
                    if (inboxRule.ForwardAsAttachmentTo) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.ForwardAsAttachmentTo
                        );
                    }
                    break;
                case 'redirectTo':
                    if (inboxRule.RedirectTo) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.RedirectTo
                        );
                    }
                    break;
            }

            // only add clauses to the edit page that have corresponding values on the inbox rule
            if (clauseValueToAdd) {
                addNewClause(clauseSection, actionClause, clauseValueToAdd);
            }
        }
        clauseSection = 'exception';
        for (let exceptionClause of conditionalExceptionClauseTypes) {
            let clauseValueToAdd: ClauseValueType;

            switch (exceptionClause) {
                case 'from':
                    if (inboxRule.ExceptIfFrom) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.ExceptIfFrom
                        );
                    }
                    break;
                case 'to':
                    if (inboxRule.ExceptIfSentTo) {
                        clauseValueToAdd = mapPeopleIdentityToReadWriteRecipientWellViewState(
                            inboxRule.ExceptIfSentTo
                        );
                    }
                    break;
                case 'onTheToOrCcLine':
                    if (inboxRule.ExceptIfMyNameInToOrCcBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'theOnlyRecipientListed':
                    if (inboxRule.ExceptIfSentOnlyToMe) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'onTheToLine':
                    if (inboxRule.ExceptIfMyNameInToBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'onTheCcLine':
                    if (inboxRule.ExceptIfMyNameInCcBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'notOnTheToLine':
                    if (inboxRule.ExceptIfMyNameNotInToBox) {
                        clauseValueToAdd = true;
                    }
                    break;
                case 'subjectIncludes':
                    if (inboxRule.ExceptIfSubjectContainsWords) {
                        clauseValueToAdd = inboxRule.ExceptIfSubjectContainsWords.slice(0);
                    }
                    break;
                case 'subjectOrBodyIncludes':
                    if (inboxRule.ExceptIfSubjectOrBodyContainsWords) {
                        clauseValueToAdd = inboxRule.ExceptIfSubjectOrBodyContainsWords.slice(0);
                    }
                    break;
                case 'inTheBody':
                    if (inboxRule.ExceptIfBodyContainsWords) {
                        clauseValueToAdd = inboxRule.ExceptIfBodyContainsWords.slice(0);
                    }
                    break;
                case 'inTheSenderAddress':
                    if (inboxRule.ExceptIfFromAddressContainsWords) {
                        clauseValueToAdd = inboxRule.ExceptIfFromAddressContainsWords.slice(0);
                    }
                    break;
                case 'inTheRecipientAddress':
                    if (inboxRule.ExceptIfRecipientAddressContainsWords) {
                        clauseValueToAdd = inboxRule.ExceptIfRecipientAddressContainsWords.slice(0);
                    }
                    break;
                case 'inTheMessageHeader':
                    if (inboxRule.ExceptIfHeaderContainsWords) {
                        clauseValueToAdd = inboxRule.ExceptIfHeaderContainsWords.slice(0);
                    }
                    break;
                case 'withImportance':
                    if (inboxRule.ExceptIfWithImportance !== 'NullImportance') {
                        clauseValueToAdd = inboxRule.ExceptIfWithImportance;
                    }
                    break;
                case 'sensitivity':
                    if (inboxRule.ExceptIfWithSensitivity !== 'NullSensitivity') {
                        clauseValueToAdd = inboxRule.ExceptIfWithSensitivity;
                    }
                    break;
                case 'flag':
                    if (inboxRule.ExceptIfFlaggedForAction !== 'NullInboxRuleMessageFlag') {
                        clauseValueToAdd = inboxRule.ExceptIfFlaggedForAction;
                    }
                    break;
                case 'type':
                    if (inboxRule.ExceptIfMessageTypeMatches !== 'NullInboxRuleMessageType') {
                        clauseValueToAdd = inboxRule.ExceptIfMessageTypeMatches;
                    }
                    break;
                case 'attachment':
                    if (inboxRule.ExceptIfHasAttachment) {
                        clauseValueToAdd = inboxRule.ExceptIfHasAttachment;
                    }
                    break;
                case 'atLeast':
                    if (inboxRule.ExceptIfWithinSizeRangeMinimum) {
                        clauseValueToAdd = inboxRule.ExceptIfWithinSizeRangeMinimum / KB_TO_B;
                    }
                    break;
                case 'atMost':
                    if (inboxRule.ExceptIfWithinSizeRangeMaximum) {
                        clauseValueToAdd = inboxRule.ExceptIfWithinSizeRangeMaximum / KB_TO_B;
                    }
                    break;
                case 'before':
                    if (inboxRule.ExceptIfReceivedBeforeDate) {
                        clauseValueToAdd = inboxRule.ExceptIfReceivedBeforeDate;
                    }
                    break;
                case 'after':
                    if (inboxRule.ExceptIfReceivedAfterDate) {
                        clauseValueToAdd = inboxRule.ExceptIfReceivedAfterDate;
                    }
                    break;
            }

            // only add clauses to the edit page that have corresponding values on the inbox rule
            if (clauseValueToAdd) {
                addNewClause(clauseSection, exceptionClause, clauseValueToAdd);
            }
        }

        initializeInboxRuleEdit();
        return;
    })
);
